import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useUserStore } from '@/stores/UserStore'
import { useApiProForecast } from '@/composables/useApiProForecast'
import { useUnitConverter } from '@/composables/useUnitConverter'
import { useDateHelper } from '@/composables/useDateHelper'
import { format } from 'date-fns'
import useStorageSystem from '@/composables/useStorageSystem'

const proForcastStore = () => {
  const NEVER_SHOW_EXTENDED_FX = [56] // Forecast IDs that do not have extended forecast available
  const IGNORE_VALID_DATE = [56]; // Forecast IDs for Pro Forecasts that we always want to show, regardless of the valid date of the forecast

  const userStore = useUserStore();
  const apiProForecast = useApiProForecast();

  const defaultSpotForecastSettings = {
    showDirectionArrow: true,
    showDirectionValue: true,
    directionValueType: 'cardinal'
  }

  const availableProForecastData = ref(null);
  const proForecastData = ref(null);
  const forecasterData = ref(null);

  const unitConverter = useUnitConverter()  
  const dateHelper = useDateHelper()

  const [recentlyViewedProForecastIds, recentlyViewedProForecastIdsLoading] = useStorageSystem( [], 'pfRecentlyViewed', 'localStorage' )
  const [spotForecastState, spotForecastStateLoading] = useStorageSystem( {}, 'pfSpotForecastState', 'apiUserStorage')
  const [spotForecastSettings, spotForecastSettingsLoading] = useStorageSystem( defaultSpotForecastSettings, 'pfSpotForecastSettings', 'apiUserStorage' )

  const availableProForecasts = computed(() => availableProForecastData.value?.forecasts || [])
  const availableProForecastsByRegion = computed(() => {
    return availableProForecasts.value.reduce((regions, forecast) => {
      if (!regions[forecast.region_name]) {
        regions[forecast.region_name] = { forecasts: [], forecasters: new Set()};
      }

      regions[forecast.region_name].forecasts.push(forecast);
      
      if (forecast.forecaster_member_id !== 0) {
        regions[forecast.region_name].forecasters.add(forecast.forecaster_member_id)
      }

      return regions;
    }, {});
  })
  const hasProForecast = computed( () => (availableProForecasts.value && availableProForecasts.value?.length > 0) || proForecastDaily .value)

  const hasExtendedProForecast = computed( () => 
    proForecastData.value && 
    Object.prototype.hasOwnProperty.call(proForecastData.value, 'extended') && 
    proForecastData.value.extended.season_status !== 'forecast not published' &&
    !NEVER_SHOW_EXTENDED_FX.includes(proForecastData.value.daily?.forecast_id)
  )

  const proForecastId = computed(() => {
    return hasProForecast.value 
      ? availableProForecasts.value[0]?.forecast_id || proForecastDaily.value.forecast_id
      : hasExtendedProForecast.value
      ? proForecastData.value.extended.forecast_id
      : null
  });
  const proForecastDaily = computed(() => proForecastData.value?.daily ? proForecastData.value.daily : false);
  const proForecastDailyNearest = computed(() => proForecastData.value?.daily_forecasts.length > 0 ? proForecastData.value.daily_forecasts[0] : false);
  const proForecastExtended = computed(() => hasExtendedProForecast.value ? proForecastData.value.extended : false);
  const spotForecasts = computed(() => proForecastDaily.value?.spot_forecasts || []);
  const requestedForecastType = computed(() => { return proForecastData.value?.requested_forecast_type });
  const isSampleForecast = computed(() => proForecastData.value?.daily?.is_sample_forecast);
  const isDailyOutOfSeason = computed(() => proForecastData.value?.daily?.season_status === 'out of season');
  const isExtendedOutOfSeason = computed(() => !requestedForecastType.value || proForecastData.value.extended?.season_status === 'out of season');
  const isExtendedPublished = computed(() => !requestedForecastType.value || proForecastData.value.extended?.season_status !== 'forecast not published');
  const isOutOfSeason = computed(() => isDailyOutOfSeason.value || isExtendedOutOfSeason.value);

  const forcastHasMultipleDailyUpdates = computed(
    () =>
      proForecastDaily.value.forecast_id === 2 ||
      proForecastDaily.value.forecast_id === 14 ||
      proForecastDaily.value.forecast_id === 67 ||
      proForecastDaily.value.forecast_id === 76
  )

  const showDailyForecast = computed(() => {
    if (forcastHasMultipleDailyUpdates.value) return true;

    if (IGNORE_VALID_DATE.includes(proForecastData.value?.daily?.forecast_id)) return true;

    if (!proForecastDaily.value) return false;

    const validDate = proForecastDaily.value.valid_time_local_formatted;
    const timeZoneName = proForecastDaily.value.tz_name_short;

    if (!timeZoneName) {
      // If no time zone name is found, log a warning and return true to show the table
      console.warn('No time zone name found for pro forecast daily data');
      return true;
    }
    
    const forecastExpirationDate = new Date(`${validDate} 20:00:00 ${timeZoneName}`);
    const expirationTimestamp = forecastExpirationDate.getTime();
    const nowTimestamp = Date.now();

    if (nowTimestamp > expirationTimestamp) return false;

    return true;
  });
  
  const proForecastName = computed(() => {
    const forecast = proForecastDaily.value || proForecastExtended.value;

    if (!forecast) return null;

    if (forecast.forecast_name) return forecast.forecast_name;

    return null;
  });

  const forecastInfo = computed(() => ({
    name: proForecastDaily.value?.forecast_name,
    validToDate: proForecastDaily.value?.valid_time_local_formatted,
    issuedTimestampFormatted: proForecastDaily.value?.issued_timestamp_local_formatted,
    nextUpdateTimeFormatted: proForecastDaily.value?.next_update_time_local_formatted,
    forecaster: proForecastDaily.value?.forecaster
  }))

  const canShowProForecast = computed(() => {
    let showProForecast = true;
            
    return showProForecast;
  });

  const proForecastMapCenterPoint = computed(() => {
    const googleMaps = window.google?.maps;

    if (!proForecastData.value || !googleMaps) return null;

    const bounds = new googleMaps.LatLngBounds();

    const forecast = proForecastData.value.daily || proForecastData.value.extended;

    if (!forecast) return null;

    const maxPoint = new googleMaps.LatLng(forecast.lat_max, forecast.lon_max);
    const minPoint = new googleMaps.LatLng(forecast.lat_min, forecast.lon_min);
    bounds.extend(maxPoint);
    bounds.extend(minPoint);

    return bounds.getCenter();
  });

  const recentlyViewedProForecasts = computed(() => {
    if (!availableProForecastData.value) return [];

    return recentlyViewedProForecastIds.value
      .map(id => availableProForecastData.value.forecasts.find((forecast) => forecast.forecast_id === id))
      .filter((forecast) => forecast !== undefined && forecast !== null);
  })  

  async function getAvailableProForecasts(lat, lon) {
    const { error, data } = await apiProForecast.getAvailable(lat, lon)

    if (error.value) return error.value

    availableProForecastData.value = data.value

    return availableProForecastData.value;
  }

  async function getProForecast(forecastId, sample) {
    const id = forecastId || proForecastId.value

    if ((id === 0 || !id) && !sample) return false;

    const { error, data } = await apiProForecast.getForecast(forecastId ? id : null, sample)

    if (error.value) return error.value

    proForecastData.value = data.value

    return proForecastData.value
  }

  async function getForecasters() {
    const { error, data } = await apiProForecast.getForecasters()

    if (error.value) return error.value

    forecasterData.value = data.value.forecasters

    return forecasterData.value
  }

  function getForecaster(id) {
    if (!forecasterData.value) return null

    const forecaster = forecasterData.value.find((f) => f.id === id)

    return forecaster || null
  }

  function buildSpotForecastModelData(spotId, utcOffset) {
    if (isOutOfSeason.value || isSampleForecast.value) return;

    const spotForecast = spotForecasts.value.find(spotForecast => spotForecast.spot_id === spotId);
    const utcOffsetString = String(utcOffset);
    const utcOffsetFormatted = utcOffsetString.length === 2 && !utcOffsetString.includes(':') ? `${utcOffsetString[0]}${utcOffsetString[1].padStart(2, '0')}:00` : `${utcOffsetString}`;

    if (!spotForecast) return null;

    const modelData = [];   

    spotForecast.forecast.forEach(forecast => {
      const min_wind_speed = unitConverter.convert('kts', userStore.units.wind, forecast.min_speed, true);
      const max_wind_speed = unitConverter.convert('kts', userStore.units.wind, forecast.max_speed, true);
      const wind_dir = unitConverter.convert('cardinal', 'degs', forecast.direction, false);
      const color = forecast.wind_color;
      
      forecast.intervals.forEach((interval, i) => {
        const hour = forecast.time_stamp_hour + i;
        const localHour = interval.indexOf('p') > -1 ? 12 + hour : hour;
        const validDate = dateHelper.datetimeToISOFormat(proForecastDaily.value.valid_time_local)
        const modelTimeLocal = format(
          validDate,
          `y-MM-dd'T'${String(localHour).padStart(2, '0')}:00:00${utcOffsetFormatted}`
        ) 
        const utcHour = new Date(modelTimeLocal).getUTCHours();
        const utcDay = new Date(modelTimeLocal).getUTCDate();
        const utcMonth = new Date(modelTimeLocal).getUTCMonth() + 1;
        const model_time_utc = format(validDate, `y-${utcMonth}-${utcDay} ${utcHour}:00:00+0000`)

        if (localHour < 8) return; // Skip hours before 8am

        modelData.push({
          model_time_utc,
          wind_dir,
          min_wind_speed,
          max_wind_speed,
          color
        })
      });
    });

    return modelData || null;
  }

  return {
    availableProForecastData,
    availableProForecasts,
    availableProForecastsByRegion,
    canShowProForecast,
    forecasterData,
    forcastHasMultipleDailyUpdates,
    forecastInfo,
    proForecastMapCenterPoint,
    hasExtendedProForecast,
    hasProForecast,
    isDailyOutOfSeason,
    isExtendedOutOfSeason,
    isExtendedPublished,
    isOutOfSeason,
    isSampleForecast,
    proForecastDaily,
    proForecastDailyNearest,
    proForecastData,
    proForecastExtended,
    proForecastId,
    proForecastName,
    recentlyViewedProForecasts,
    recentlyViewedProForecastIds,
    recentlyViewedProForecastIdsLoading,
    requestedForecastType,
    spotForecastState,
    spotForecastStateLoading,
    spotForecastSettings,
    spotForecastSettingsLoading,
    showDailyForecast,
    buildSpotForecastModelData,
    getAvailableProForecasts,
    getForecaster,
    getForecasters,
    getProForecast
  }
}

export const useProForecastStore = (storeId) => {
  const storeName = storeId ? `proForecastStore-${storeId}` : 'proForecastStore';
  
  const store = defineStore(storeName, proForcastStore);

  return store();
}
